import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  fetchAiOutreachFocuses,
  fetchAiOutreachAccounts,
  runAiOutreachAnalysis,
  fetchAiOutreachAnalysisJob,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Clock, Users, ExternalLink, CheckCircle2, Flame, TrendingDown, MessageSquareWarning, Lightbulb, Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FOCUS_META = {
  hot_leads:        { icon: Flame,                tone: "text-orange-600 bg-orange-50 border-orange-200" },
  late_responses:   { icon: TrendingDown,         tone: "text-amber-600 bg-amber-50 border-amber-200" },
  missed_intent:    { icon: Lightbulb,            tone: "text-blue-600 bg-blue-50 border-blue-200" },
  bad_handling:     { icon: MessageSquareWarning, tone: "text-rose-600 bg-rose-50 border-rose-200" },
  success_patterns: { icon: Trophy,               tone: "text-emerald-600 bg-emerald-50 border-emerald-200" },
};

const SEVERITY_TONE = {
  high:   "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low:    "bg-slate-50 text-slate-700 border-slate-200",
};

export default function AiAssistantSection({ clientId }) {
  const [accountId, setAccountId] = useState("all");
  const [period, setPeriod] = useState("30");
  const [focus, setFocus] = useState("all");
  const [maxConvs, setMaxConvs] = useState("50");
  const [report, setReport] = useState(null);
  const [analysisJobId, setAnalysisJobId] = useState(null);

  const { data: focuses } = useQuery({
    queryKey: ["ai-focuses"],
    queryFn: fetchAiOutreachFocuses,
  });
  const { data: accountsData } = useQuery({
    queryKey: ["ai-accounts", clientId],
    queryFn: () => fetchAiOutreachAccounts(clientId),
  });

  const analyzeMut = useMutation({
    mutationFn: () =>
      runAiOutreachAnalysis(clientId, {
        account_id: accountId === "all" ? null : Number(accountId),
        period_days: Number(period),
        focus,
        max_conversations: Number(maxConvs),
      }),
    onSuccess: (data) => {
      setReport(null);
      setAnalysisJobId(data.job_id);
      toast.success("Trayendo conversaciones de Skylead…");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Falló el análisis IA"),
  });

  const { data: jobData } = useQuery({
    queryKey: ["ai-analysis-job", clientId, analysisJobId],
    queryFn: () => fetchAiOutreachAnalysisJob(clientId, analysisJobId),
    enabled: !!analysisJobId,
    refetchInterval: (q) => (q.state.data?.status === "running" ? 4000 : false),
  });

  useEffect(() => {
    if (!jobData || !analysisJobId) return;
    if (jobData.status === "done" && jobData.report) {
      setReport(jobData.report);
      setAnalysisJobId(null);
      const secs = Math.round((jobData.report.duration_ms || 0) / 1000);
      toast.success(`Análisis listo · ${jobData.report.input_count} conversaciones · ${secs}s`);
    } else if (jobData.status === "error") {
      setAnalysisJobId(null);
      toast.error(`Falló el análisis: ${jobData.error || "error desconocido"}`);
    }
  }, [jobData, analysisJobId]);

  const accounts = accountsData?.accounts || [];
  const focusList = focuses?.focuses || [];
  const isAnalyzing = analyzeMut.isPending || (!!analysisJobId && jobData?.status === "running");

  return (
    <div className="space-y-5" data-testid="ai-assistant-section">
      {/* Header banner */}
      <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-indigo-100 rounded-md p-5">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-md bg-indigo-600 text-white flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-indigo-700 font-semibold">
              <span>Asistente IA · Outreach</span>
              <span className="text-indigo-300">·</span>
              <span className="text-slate-500 lowercase tracking-normal">Claude Sonnet 4.6</span>
            </div>
            <h3 className="font-display text-lg font-semibold text-slate-900 mt-0.5">Analizá conversaciones de Skylead con IA</h3>
            <p className="text-xs text-slate-600 mt-1">
              Cada análisis trae las conversaciones <span className="font-semibold">en vivo desde Skylead</span> según los filtros aplicados (sin caché). Detecta oportunidades calientes, respuestas tardías, intención de compra perdida y patrones generales.
            </p>
          </div>
        </div>
      </div>

      {/* Filters + run analysis */}
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
              <Users className="h-3 w-3" /> Cuenta
            </label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="h-9 text-sm" data-testid="ai-account-select">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cuentas</SelectItem>
                {accounts.map((a) => (
                  <SelectItem key={a.account_id} value={String(a.account_id)}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
              <Clock className="h-3 w-3" /> Período
            </label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-9 text-sm" data-testid="ai-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 días</SelectItem>
                <SelectItem value="15">Últimos 15 días</SelectItem>
                <SelectItem value="30">Últimos 30 días</SelectItem>
                <SelectItem value="60">Últimos 60 días</SelectItem>
                <SelectItem value="90">Últimos 90 días</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Enfoque
            </label>
            <Select value={focus} onValueChange={setFocus}>
              <SelectTrigger className="h-9 text-sm" data-testid="ai-focus-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {focusList.map((f) => (
                  <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
              <Flame className="h-3 w-3" /> Volumen
            </label>
            <Select value={maxConvs} onValueChange={setMaxConvs}>
              <SelectTrigger className="h-9 text-sm" data-testid="ai-max-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">20 conversaciones (rápido)</SelectItem>
                <SelectItem value="50">50 conversaciones (recomendado)</SelectItem>
                <SelectItem value="100">100 conversaciones</SelectItem>
                <SelectItem value="150">150 conversaciones (máx)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
          onClick={() => analyzeMut.mutate()}
          disabled={isAnalyzing}
          data-testid="ai-analyze-btn"
        >
          {isAnalyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {isAnalyzing
            ? (jobData?.stage === "done" ? "Finalizando…" : "Trayendo conversaciones + analizando con Claude…")
            : "Analizar con IA"}
        </Button>
        {isAnalyzing && (
          <p className="text-[11px] text-slate-500 text-center">
            Suele tardar entre 30 segundos y 2 minutos según el volumen elegido. Podés cerrar la pestaña, va a seguir corriendo.
          </p>
        )}
      </div>

      {report && <ReportView report={report} />}
    </div>
  );
}

function ReportView({ report }) {
  const categories = report.categories || [];
  return (
    <div className="space-y-4" data-testid="ai-report">
      <div className="bg-slate-900 text-white rounded-md p-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400 mb-1">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
          <span>Resumen ejecutivo</span>
          <span className="text-slate-500">·</span>
          <span>{report.input_count} conversaciones analizadas</span>
          {report.duration_ms != null && (
            <>
              <span className="text-slate-500">·</span>
              <span>{Math.round(report.duration_ms / 1000)}s</span>
            </>
          )}
        </div>
        <p className="text-sm leading-relaxed text-slate-100">{report.summary || "Sin resumen disponible."}</p>
      </div>

      {categories.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-sm text-slate-500">
          La IA no encontró insights destacables con los filtros aplicados.
        </div>
      )}

      {categories.map((cat) => {
        const meta = FOCUS_META[cat.key] || { icon: Sparkles, tone: "text-slate-600 bg-slate-50 border-slate-200" };
        const Icon = meta.icon;
        const items = cat.items || [];
        return (
          <div key={cat.key} className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className={`px-4 py-3 border-b flex items-center justify-between ${meta.tone}`}>
              <div className="flex items-center gap-2 font-semibold text-sm">
                <Icon className="h-4 w-4" />
                <span>{cat.label}</span>
              </div>
              <span className="text-xs font-mono">{items.length}</span>
            </div>
            <div className="divide-y divide-slate-100">
              {items.map((it, idx) => (
                <div key={idx} className="p-4 hover:bg-slate-50 transition-colors" data-testid="ai-report-item">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900">{it.lead_name || "Sin nombre"}</span>
                        {it.company && <span className="text-xs text-slate-500">· {it.company}</span>}
                        {it.severity && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${SEVERITY_TONE[it.severity] || SEVERITY_TONE.low}`}>
                            {it.severity.toUpperCase()}
                          </span>
                        )}
                        {it.account && (
                          <span className="text-[10px] text-slate-400">por {it.account}</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-800 mt-1.5">{it.headline}</p>
                      {it.evidence && (
                        <blockquote className="mt-2 text-xs text-slate-600 border-l-2 border-slate-300 pl-2.5 italic">
                          &ldquo;{it.evidence}&rdquo;
                        </blockquote>
                      )}
                      {it.recommended_action && (
                        <div className="mt-2 text-xs bg-indigo-50 border border-indigo-100 text-indigo-900 rounded p-2">
                          <span className="font-semibold">Acción recomendada: </span>
                          {it.recommended_action}
                        </div>
                      )}
                    </div>
                    {it.linkedin_url && (
                      <a
                        href={it.linkedin_url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
                      >
                        Abrir <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {report.patterns && report.patterns.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold flex items-center gap-1.5 mb-2">
            <Lightbulb className="h-3.5 w-3.5" />
            <span>Patrones generales detectados</span>
          </div>
          <ul className="space-y-1.5 text-sm text-amber-900">
            {report.patterns.map((p, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-amber-500">•</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
